/**
 * Util to clean object of keys that have empty values.
 */

export type DeepCleanObjOpts = {
  includeNull: boolean;
  includeUndefined: boolean;
  includeEmptyArr: boolean;
  includeEmptyObj: boolean;
  includeEmptyString: boolean;
};

export const deepCleanObjDefaultOpts = {
  includeNull: true,
  includeUndefined: true,
  includeEmptyArr: false,
  includeEmptyObj: false,
  includeEmptyString: false,
};

export const deepCleanObj = (obj: Record<string, any>, opts: DeepCleanObjOpts = deepCleanObjDefaultOpts) => {
  opts = {
    ...deepCleanObjDefaultOpts,
    ...opts,
  };

  const isPlainObject = (value) => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  };

  const isEmptyValue = (value) => {
    if (opts.includeNull && value === null) {
      return true;
    }
    if (opts.includeUndefined && value === undefined) {
      return true;
    }
    if (opts.includeEmptyArr && Array.isArray(value) && value.length === 0) {
      return true;
    }
    if (opts.includeEmptyObj && isPlainObject(value) && Object.keys(value).length === 0) {
      return true;
    }
    if (opts.includeEmptyString && typeof value === 'string' && value.trim() === '') {
      return true;
    }

    return false;
  };

  const cleanObject = (inputObj) => {
    if (Array.isArray(inputObj)) {
      for (let i = inputObj.length - 1; i >= 0; i--) {
        if (isEmptyValue(inputObj[i])) {
          inputObj.splice(i, 1);
        } else if (isPlainObject(inputObj[i]) || Array.isArray(inputObj[i])) {
          cleanObject(inputObj[i]);
        }
      }
    } else if (isPlainObject(inputObj)) {
      for (const key in inputObj) {
        if (Object.hasOwnProperty.call(inputObj, key)) {
          if (isEmptyValue(inputObj[key])) {
            delete inputObj[key];
          } else if (isPlainObject(inputObj[key]) || Array.isArray(inputObj[key])) {
            cleanObject(inputObj[key]);
            // If after cleaning the object, it's empty, delete the key
            if (opts.includeEmptyObj && Object.keys(inputObj[key]).length === 0) {
              delete inputObj[key];
            }
          }
        }
      }
    }
  };

  cleanObject(obj);

  return obj;
};
